import React, { useEffect, useRef } from "react";
import lightLogo from "@packages/assets/reversed-light.png";
import darkLogo from "@packages/assets/reverse-logo.png";
import { useTheme } from "../../hooks/useTheme";

interface WaterLoaderProps {
  progress: number;
  size?: number;
  duration?: number; // in milliseconds
  borderColor?: string;
}

const WaterLoader: React.FC<WaterLoaderProps> = ({
  progress,
  size = 200,
  duration = 2000, // default 2 seconds
  borderColor,
}) => {
  const { theme } = useTheme();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const logoRef = useRef<HTMLImageElement | null>(null);
  const dropletsRef = useRef<
    Array<{
      x: number;
      y: number;
      radius: number;
      speed: number;
      opacity: number;
      angle?: number;
      rotationSpeed?: number;
    }>
  >([]);
  const animationFrameRef = useRef<number>();
  const lastTimeRef = useRef(0);
  const waveOffsetRef = useRef(0);
  const currentProgressRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr * 2;
    canvas.height = size * dpr * 2;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    ctx.scale(dpr * 2, dpr * 2);

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    if (!logoRef.current) {
      const logoImg = new Image();
      logoImg.src = theme === "light" ? lightLogo : darkLogo;
      logoRef.current = logoImg;
    }

    const animate = (currentTime: number) => {
      if (!ctx || !logoRef.current) return;

      const deltaTime = currentTime - lastTimeRef.current;
      lastTimeRef.current = currentTime;

      // Smooth progress animation based on duration
      const progressDiff = progress - currentProgressRef.current;
      if (Math.abs(progressDiff) > 0.01) {
        const progressStep = (deltaTime / duration) * 50;
        const step =
          progressDiff > 0
            ? Math.min(progressStep, progressDiff)
            : Math.max(-progressStep, progressDiff);

        currentProgressRef.current += step;
      }

      const progressWave = Math.sin(waveOffsetRef.current * 2) * 4;
      const fillHeight =
        (currentProgressRef.current / 100) * size +
        progressWave * (1 - Math.abs(currentProgressRef.current - 50) / 50);

      waveOffsetRef.current += 0.01;

      ctx.clearRect(0, 0, size, size);

      // Logo outline - sadece iç kısım
      ctx.save();
      ctx.drawImage(logoRef.current, 0, 0, size, size);
      ctx.globalCompositeOperation = "source-in";
      ctx.fillStyle = "transparent";
      ctx.fillRect(0, 0, size, size);
      ctx.restore();

      // Water effect - sadece iç kısım
      ctx.save();
      ctx.drawImage(logoRef.current, 0, 0, size, size);
      ctx.globalCompositeOperation = "source-atop";

      // Create water mask with smooth edges
      ctx.beginPath();
      ctx.rect(0, size - fillHeight, size, fillHeight);
      ctx.clip();

      // Water gradient
      const waterGradient = ctx.createLinearGradient(
        0,
        size - fillHeight,
        0,
        size,
      );
      // waterGradient.addColorStop(0, "#FFF3E0"); // Ethereal light orange glow
      // waterGradient.addColorStop(0.15, "#FFE0B2"); // Soft peach transition
      // waterGradient.addColorStop(0.3, "#FFB74D"); // Warm amber
      // waterGradient.addColorStop(0.5, "#FF9800"); // Vibrant pure orange
      // waterGradient.addColorStop(0.65, "#FB8C00"); // Deep mandarin
      waterGradient.addColorStop(0, "#fff"); // Ethereal light orange glow
      waterGradient.addColorStop(0.15, "#FFE0B2"); // Soft peach transition
      waterGradient.addColorStop(0.3, "#FFB74D"); // Warm amber
      waterGradient.addColorStop(0.5, "#FF9800"); // Vibrant pure orange
      waterGradient.addColorStop(0.65, "#FB8C00"); // Deep mandarin
      waterGradient.addColorStop(0.85, "#F57C00"); // Rich burnt orange
      waterGradient.addColorStop(0.92, "#EF6C00"); // Intense deep orange
      waterGradient.addColorStop(1, "#EF6C00"); // Dark copper orange

      ctx.fillStyle = waterGradient;
      ctx.fillRect(0, 0, size, size);

      // Enhanced wave pattern - daha komplex ve smooth dalgalar
      ctx.beginPath();
      ctx.moveTo(0, size);

      // Daha karmaşık ve doğal dalga hareketi
      for (let i = 0; i <= size; i += 0.5) {
        const normalizedHeight = (size - (size - fillHeight)) / size;
        const amplitudeFactor = Math.min(1, normalizedHeight * 2);

        // Azaltılmış dalga yüksekliği
        const waveHeight =
          (Math.sin(i * 0.01 + waveOffsetRef.current) * 15 +
            Math.sin(i * 0.02 + waveOffsetRef.current * 1.8) * 10 +
            Math.sin(i * 0.005 + waveOffsetRef.current * 0.4) * 20) *
          amplitudeFactor;

        // Daha yumuşak üst çizgi dalgası
        const topLineWave =
          Math.sin(i * 0.03 + waveOffsetRef.current * 2) * 15 +
          Math.sin(i * 0.06 + waveOffsetRef.current * 1.5) * 10;

        // Kenarlardan yumuşatma
        const edgeFactor = Math.min(i, size - i) / (size * 0.2);
        const smoothing = Math.min(1, edgeFactor);

        ctx.lineTo(
          i,
          size - fillHeight + (waveHeight + topLineWave) * smoothing * 0.7,
        );
      }

      // Add enhanced reflection effect with stronger top highlight
      const reflection = ctx.createLinearGradient(
        0,
        size - fillHeight,
        0,
        size,
      );
      reflection.addColorStop(0, "rgba(255, 255, 255, 0.35)"); // Daha güçlü üst yansıma
      reflection.addColorStop(0.15, "rgba(255, 255, 255, 0.25)");
      reflection.addColorStop(0.3, "rgba(255, 255, 255, 0.15)");
      reflection.addColorStop(0.5, "rgba(255, 255, 255, 0.08)");
      reflection.addColorStop(1, "rgba(255, 255, 255, 0)");

      ctx.lineTo(size, size);
      ctx.closePath();
      ctx.fill();

      // İlk reflection katmanı
      ctx.fillStyle = reflection;
      ctx.fill();

      // İkinci reflection katmanı - daha dinamik efekt için
      const reflection2 = ctx.createLinearGradient(
        0,
        size - fillHeight,
        0,
        size,
      );
      reflection2.addColorStop(
        0,
        `rgba(255, 255, 255, ${Math.abs(Math.sin(waveOffsetRef.current)) * 0.1})`,
      );
      reflection2.addColorStop(1, "rgba(255, 255, 255, 0)");

      ctx.fillStyle = reflection2;
      ctx.fill();

      // Droplet ayarları - daha zarif ve etkileyici
      if (Math.random() < 0.08 && currentProgressRef.current < 98) {
        // Progress neredeyse doluyken damlacıkları azalt
        createDroplet();
      }

      dropletsRef.current = dropletsRef.current.filter((droplet) => {
        if (droplet.y > size - fillHeight) {
          // Daha zarif splash efekti
          for (let i = 0; i < 5; i++) {
            dropletsRef.current.push({
              x: droplet.x + (Math.random() - 0.5) * 30,
              y: size - fillHeight,
              radius: droplet.radius * 0.45,
              speed: -droplet.speed * 0.65,
              opacity: 0.95,
              angle: Math.random() * Math.PI * 2,
              rotationSpeed: (Math.random() - 0.5) * 0.15,
            });
          }
          return false;
        }

        ctx.save();
        if (droplet.angle !== undefined) {
          ctx.translate(droplet.x, droplet.y);
          ctx.rotate(droplet.angle);
          ctx.translate(-droplet.x, -droplet.y);
          if (droplet.rotationSpeed) droplet.angle += droplet.rotationSpeed;
        }

        // Enhanced droplet glow
        const dropletGradient = ctx.createRadialGradient(
          droplet.x,
          droplet.y,
          0,
          droplet.x,
          droplet.y,
          droplet.radius * 3,
        );
        dropletGradient.addColorStop(
          0,
          `rgba(254, 243, 199, ${droplet.opacity})`,
        );
        dropletGradient.addColorStop(
          0.4,
          `rgba(253, 230, 138, ${droplet.opacity * 0.8})`,
        );
        dropletGradient.addColorStop(
          0.7,
          `rgba(252, 211, 77, ${droplet.opacity * 0.5})`,
        );
        dropletGradient.addColorStop(1, `rgba(252, 211, 77, 0)`);

        ctx.beginPath();
        ctx.fillStyle = dropletGradient;
        ctx.arc(droplet.x, droplet.y, droplet.radius * 3, 0, Math.PI * 2);
        ctx.fill();

        // Actual droplet with shine
        ctx.beginPath();
        ctx.fillStyle = `rgba(254, 243, 199, ${droplet.opacity})`;
        ctx.arc(droplet.x, droplet.y, droplet.radius, 0, Math.PI * 2);
        ctx.fill();

        // Add shine to droplet
        const shineGradient = ctx.createLinearGradient(
          droplet.x - droplet.radius,
          droplet.y - droplet.radius,
          droplet.x + droplet.radius,
          droplet.y + droplet.radius,
        );
        shineGradient.addColorStop(
          0,
          `rgba(255, 255, 255, ${droplet.opacity * 0.5})`,
        );
        shineGradient.addColorStop(1, `rgba(255, 255, 255, 0)`);
        ctx.fillStyle = shineGradient;
        ctx.fill();

        ctx.restore();

        droplet.y += droplet.speed * 0.7; // Daha da yavaş damlacıklar
        droplet.opacity -= 0.008; // Çok daha yavaş kaybolma

        return droplet.opacity > 0;
      });

      ctx.restore();

      // Enhanced glow effect with multiple layers
      const glowGradient1 = ctx.createRadialGradient(
        size / 2,
        size / 2,
        0,
        size / 2,
        size / 2,
        size / 2,
      );
      glowGradient1.addColorStop(
        0,
        theme === "light"
          ? "rgba(253, 230, 138, 0.4)"
          : "rgba(234, 179, 8, 0.4)",
      );
      glowGradient1.addColorStop(
        0.5,
        theme === "light"
          ? "rgba(252, 211, 77, 0.2)"
          : "rgba(202, 138, 4, 0.2)",
      );
      glowGradient1.addColorStop(
        1,
        theme === "light" ? "rgba(251, 191, 36, 0)" : "rgba(161, 98, 7, 0)",
      );

      ctx.globalCompositeOperation = "source-over";
      // ctx.fillStyle = glowGradient1;
      // ctx.fillRect(0, 0, size, size);

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    const createDroplet = () => {
      if (dropletsRef.current.length < 15) {
        // Daha az ama daha etkileyici damlacıklar
        dropletsRef.current.push({
          x: Math.random() * size,
          y: -10,
          radius: Math.random() * 2.2 + 1,
          speed: Math.random() * 1.8 + 1.5, // Daha yavaş damlacıklar
          opacity: 1,
          angle: Math.random() * Math.PI * 2,
          rotationSpeed: (Math.random() - 0.5) * 0.06,
        });
      }
    };

    if (logoRef.current.complete) {
      animationFrameRef.current = requestAnimationFrame(animate);
    } else {
      logoRef.current.onload = () => {
        animationFrameRef.current = requestAnimationFrame(animate);
      };
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [progress, size, duration]);

  return (
    <div className="relative mb-8">
      <canvas ref={canvasRef} className={`rounded-full`} />
    </div>
  );
};

export default WaterLoader;
